import type { Supplier } from '../types'
import { fetchAllFromTable } from './fetchAllFromTable'
import { SUPPLIER_LIST_SELECT, SUPPLIER_RA_LIST_SELECT } from './supplierSelect'

const DEFAULT_TTL_MS = 60_000

type CacheEntry = {
  select: string
  rows: Supplier[]
  at: number
  inflight: Promise<Supplier[]> | null
}

const caches = new Map<string, CacheEntry>()

function entryKey(select: string): string {
  return select
}

/**
 * Shared session roster for Risk Analyst + Early Signals pages.
 * Dedupes concurrent loads and avoids re-paging ~2k rows on every ask.
 */
export async function fetchSupplierRoster(options?: {
  select?: string
  ttlMs?: number
  force?: boolean
}): Promise<Supplier[]> {
  const select = options?.select ?? SUPPLIER_LIST_SELECT
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS
  const key = entryKey(select)
  const now = Date.now()
  let entry = caches.get(key)

  if (!options?.force && entry && now - entry.at < ttlMs && entry.rows.length > 0) {
    return entry.rows
  }

  if (!options?.force && entry?.inflight) {
    return entry.inflight
  }

  if (!entry) {
    entry = { select, rows: [], at: 0, inflight: null }
    caches.set(key, entry)
  }

  const inflight = fetchAllFromTable<Supplier>('suppliers', select, { orderBy: 'name' })
    .then((rows) => {
      entry!.rows = rows
      entry!.at = Date.now()
      entry!.inflight = null
      return rows
    })
    .catch((err) => {
      entry!.inflight = null
      throw err
    })

  entry.inflight = inflight
  return inflight
}

export function fetchRaSupplierRoster(options?: { force?: boolean; ttlMs?: number }): Promise<Supplier[]> {
  return fetchSupplierRoster({
    select: SUPPLIER_RA_LIST_SELECT,
    force: options?.force,
    ttlMs: options?.ttlMs,
  })
}

export function invalidateSupplierRosterCache(): void {
  caches.clear()
}
